import type { Pcg32 } from '../../../sim/rng';
import type { PixelCamera } from '../../camera/PixelCamera';

/** Trauma 0..1 is squared into texels and re-rolled every other 60 Hz frame. */
const SHAKE_TEXELS = 6;
const SHAKE_DECAY = 1.7;
const SHAKE_HOLD = 1 / 30;
/** Blasts farther than this from the camera focus do not shake the screen. */
const SHAKE_RANGE = 45;

/** Screen shake in whole texels (05 §8.2), so it never turns into a sub-texel smear. */
export class Shake {
  /** Offset in metres (x, z) for the camera focus this frame; decays by itself. */
  readonly offset = { x: 0, z: 0 };
  /** Off for players who asked for a still camera: blasts add nothing and any shake in progress stops. */
  enabled = true;
  private trauma = 0;
  private clock = 0;
  private hasFocus = false;
  private focusX = 0;
  private focusZ = 0;

  constructor(private readonly rng: Pcg32) {}

  /** Remember where the camera looks, so distant blasts shake less. */
  track(camera: PixelCamera): void {
    this.hasFocus = true;
    this.focusX = camera.focus.x;
    this.focusZ = camera.focus.z;
  }

  add(x: number, z: number, amount: number): void {
    if (!this.enabled) return;
    const near = this.hasFocus ? Math.max(0, 1 - Math.hypot(x - this.focusX, z - this.focusZ) / SHAKE_RANGE) : 1;
    this.trauma = Math.min(1, this.trauma + amount * near);
  }

  step(dt: number, camera: PixelCamera): void {
    this.trauma = this.enabled ? Math.max(0, this.trauma - dt * SHAKE_DECAY) : 0;
    this.clock += dt;
    if (this.trauma <= 0) {
      this.offset.x = 0;
      this.offset.z = 0;
      return;
    }
    if (this.clock < SHAKE_HOLD) return;
    this.clock = 0;
    // Whole texels only: one texel is 1/ppm across the screen and 1/(ppm sin pitch) up it.
    const amp = this.trauma * this.trauma * SHAKE_TEXELS;
    const ppm = camera.pixelsPerMetre;
    this.offset.x = (Math.round(this.rng.range(-amp, amp)) + 0) / ppm;
    this.offset.z = (Math.round(this.rng.range(-amp, amp)) + 0) / (ppm * camera.sinPitch);
  }
}
